import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Send, Bot, User, Sparkles, RotateCcw } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const scenarioTabs = [
  { id: "negotiation", label: "Salary Negotiation" },
  { id: "sales", label: "Enterprise Sales" },
  { id: "feedback", label: "Difficult Feedback" },
  { id: "objection", label: "Handling Objections" },
];

const scenarioDescriptions = {
  negotiation: "Practice negotiating a raise with your manager after a successful project delivery.",
  sales: "Close a deal with a skeptical CTO evaluating your SaaS product against competitors.",
  feedback: "Give constructive feedback to an underperforming team member while maintaining rapport.",
  objection: "A potential client is pushing back on your proposal. Overcome their objections gracefully.",
};

function ScenarioDescription({ scenario }) {
  if (scenario === "negotiation") {
    return <>Practice negotiating a raise with your manager after<br className="hidden lg:inline" /> a successful project delivery.</>;
  }
  return scenarioDescriptions[scenario];
}

const openingMessages = {
  negotiation: "Hi, thanks for setting up this meeting. I understand you wanted to discuss your compensation. What's on your mind?",
  sales: "Thanks for the demo yesterday. I'll be honest — we're looking at two other vendors and I'm not yet convinced your solution justifies the premium pricing. What would you say to that?",
  feedback: "Hey, you mentioned wanting to catch up. What's this about?",
  objection: "Look, I like what you've presented, but our budget is frozen until Q3 and honestly I'm not sure we need this right now. Can you convince me otherwise?",
};

export default function InteractiveDemoSection() {
  const [activeScenario, setActiveScenario] = useState("negotiation");
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [feedback, setFeedback] = useState(null);
  const [started, setStarted] = useState(false);
  const scrollRef = useRef(null);
  const inputRef = useRef(null);

  const resetChat = (scenario) => {
    setMessages([]);
    setSessionId(null);
    setFeedback(null);
    setStarted(false);
    setInput("");
    if (scenario) setActiveScenario(scenario);
  };

  const startScenario = () => {
    const opening = openingMessages[activeScenario];
    setMessages([{ role: "ai", content: opening }]);
    setStarted(true);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  useEffect(() => {
    if (scrollRef.current) {
      const viewport = scrollRef.current.querySelector("[data-radix-scroll-area-viewport]");
      if (viewport) viewport.scrollTop = viewport.scrollHeight;
    }
  }, [messages]);

  const sendMessage = async () => {
    if (!input.trim() || loading) return;
    const userMsg = input.trim();
    setInput("");
    setMessages((prev) => [...prev, { role: "user", content: userMsg }]);
    setLoading(true);
    setFeedback(null);

    try {
      const history = messages
        .filter((m) => m.role === "user")
        .map((m) => ({ role: "user", content: m.content }));

      const res = await axios.post(`${API}/chat`, {
        message: userMsg,
        scenario: activeScenario,
        session_id: sessionId,
        history,
      });

      setSessionId(res.data.session_id);
      setMessages((prev) => [...prev, { role: "ai", content: res.data.reply }]);
      if (res.data.feedback) setFeedback(res.data.feedback);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: "ai", content: "I'm having trouble connecting right now. Please try again in a moment." },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <section id="demo" data-testid="demo-section" className="py-[45px] md:py-[90px] stripe-bg-white relative overflow-hidden">
      <div className="absolute inset-0 hero-glow opacity-60" />

      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ ease: [0.22, 1, 0.36, 1] }}
          className="text-center mb-12"
        >
          <div className="section-overline mb-4">Interactive Demo</div>
          <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-navy mb-4">
            Try it yourself
          </h2>
          <p className="text-base md:text-lg text-stripe-text-secondary max-w-lg mx-auto">
            Pick a scenario and start a conversation with our AI. Experience real-time adaptive dialogue.
          </p>
        </motion.div>

        {/* Scenario tabs */}
        <div className="grid grid-cols-2 md:flex md:flex-wrap justify-center gap-2 mb-8 max-w-md md:max-w-none mx-auto">
          {scenarioTabs.map((tab) => (
            <button
              key={tab.id}
              data-testid={`scenario-tab-${tab.id}`}
              onClick={() => resetChat(tab.id)}
              className={`px-5 py-2.5 rounded-full text-sm font-medium transition-all duration-300 ${
                activeScenario === tab.id
                  ? "bg-stripe-primary text-white shadow-md shadow-stripe-primary/20"
                  : "bg-white text-stripe-text-secondary border border-stripe-border hover:border-stripe-primary/30 hover:text-navy hover:shadow-sm"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Chat interface */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
          className="stripe-card rounded-2xl overflow-hidden max-w-3xl mx-auto"
        >
          {/* Chat header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-stripe-border bg-slate-50/50">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-stripe-primary/10 flex items-center justify-center">
                <Bot className="w-4 h-4 text-stripe-primary" />
              </div>
              <div>
                <div className="text-navy text-sm font-medium">AI Coach</div>
                <div className="text-stripe-text-muted text-xs"><ScenarioDescription scenario={activeScenario} /></div>
              </div>
            </div>
            {started && (
              <Button
                data-testid="reset-chat-button"
                variant="ghost"
                size="sm"
                onClick={() => resetChat()}
                className="text-stripe-text-muted hover:text-navy hover:bg-slate-100"
              >
                <RotateCcw className="w-4 h-4 mr-1" />
                Reset
              </Button>
            )}
          </div>

          {/* Chat messages */}
          <ScrollArea ref={scrollRef} className="h-[360px]" data-testid="chat-messages-area">
            <div className="p-6 space-y-4">
              {!started ? (
                <div className="flex flex-col items-center justify-center h-[280px] text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-stripe-primary/10 to-cyan-400/10 flex items-center justify-center mb-4">
                    <Sparkles className="w-8 h-8 text-stripe-primary" />
                  </div>
                  <h3 className="font-heading text-lg font-semibold text-navy mb-2">
                    Ready to practice?
                  </h3>
                  <p className="text-sm text-stripe-text-secondary max-w-sm mb-6">
                    <ScenarioDescription scenario={activeScenario} />
                  </p>
                  <Button
                    data-testid="start-scenario-button"
                    onClick={startScenario}
                    className="bg-stripe-primary hover:bg-stripe-primary-hover text-white rounded-full px-6 shadow-md shadow-stripe-primary/20 hover:shadow-lg hover:shadow-stripe-primary/25 transition-all duration-300"
                  >
                    Begin Practice
                  </Button>
                </div>
              ) : (
                <>
                  {messages.map((msg, i) => (
                    <div
                      key={i}
                      data-testid={`chat-message-${i}`}
                      className={`flex gap-3 ${msg.role === "user" ? "justify-end" : ""}`}
                    >
                      {msg.role === "ai" && (
                        <div className="w-7 h-7 rounded-lg bg-stripe-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <Bot className="w-3.5 h-3.5 text-stripe-primary" />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed ${
                          msg.role === "user"
                            ? "chat-bubble-user rounded-tr-sm"
                            : "chat-bubble-ai rounded-tl-sm"
                        }`}
                      >
                        {msg.content}
                      </div>
                      {msg.role === "user" && (
                        <div className="w-7 h-7 rounded-lg bg-stripe-primary/10 flex items-center justify-center shrink-0 mt-0.5">
                          <User className="w-3.5 h-3.5 text-stripe-primary" />
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3">
                      <div className="w-7 h-7 rounded-lg bg-stripe-primary/10 flex items-center justify-center shrink-0">
                        <Bot className="w-3.5 h-3.5 text-stripe-primary" />
                      </div>
                      <div className="chat-bubble-ai rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1.5">
                        <span className="typing-dot w-2 h-2 rounded-full bg-stripe-primary" />
                        <span className="typing-dot w-2 h-2 rounded-full bg-stripe-primary" />
                        <span className="typing-dot w-2 h-2 rounded-full bg-stripe-primary" />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </ScrollArea>

          {/* Feedback bar */}
          {feedback && (
            <div className="mx-6 mb-3 px-4 py-3 bg-emerald-50 border border-emerald-100 rounded-xl flex items-start gap-2">
              <Sparkles className="w-4 h-4 text-emerald-600 shrink-0 mt-0.5" />
              <p className="text-xs text-emerald-700 leading-relaxed" data-testid="coaching-feedback">
                <span className="font-semibold">Coaching: </span>
                {feedback.text}
              </p>
            </div>
          )}

          {/* Input area */}
          {started && (
            <div className="p-4 border-t border-stripe-border bg-slate-50/30">
              <div className="flex gap-3">
                <Input
                  ref={inputRef}
                  data-testid="chat-input"
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="Type your response..."
                  disabled={loading}
                  className="flex-1 bg-white border-stripe-border text-navy placeholder:text-stripe-text-muted focus:border-stripe-primary focus:ring-stripe-primary/20 rounded-xl"
                />
                <Button
                  data-testid="send-message-button"
                  onClick={sendMessage}
                  disabled={loading || !input.trim()}
                  className="bg-stripe-primary hover:bg-stripe-primary-hover text-white rounded-xl px-4 shadow-md shadow-stripe-primary/15 disabled:opacity-40 transition-all duration-300"
                >
                  <Send className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
}
